import { createTheme } from "@material-ui/core/styles";

interface IExtraColors {
  default: string;
  grey: string;
  mediumGrey: string;
  lighterGrey: string;
  lightBlue: string;
  lightBorder: string;
  lightBorderActive: string;
  borderColor: string;
  warning: string;
  warningSoft: string;
  success: string;
  successSoft: string;
  infoSoft: string;
  backgroundGrey: string;
  subtleContrastText: string;
  insightAccent: string;
  spiderGraphBackground: string;
  accentSoft: string;
  win: string;
  winSoft: string;
  draw: string;
  drawSoft: string;
  loss: string;
  lossSoft: string;
}

interface ILayout {
  maxContentWidth: number;
  navDrawerWidthClosed: number;
  navDrawerWidthOpen: number;
  headerHeight: number;
  tabsHeight: number;
  listHeaderBarHeight: number;
  footerHeight: number;
  menuDrawerWidth: number;
}

declare module "@material-ui/core/styles/createPalette" {
  /* Adding an extra property to the Palette interface, based on a 'primary' type */
  // tslint:disable-next-line
  interface Palette {
    extra: IExtraColors;
  }

  // tslint:disable-next-line
  interface PaletteOptions {
    extra: IExtraColors;
  }
}

declare module "@material-ui/core/styles/createTypography" {
  /* Adding an extra property to the Palette interface, based on a 'primary' type */
  // tslint:disable-next-line
  interface Typography {
    alternateFontFamily: string;
  }

  // tslint:disable-next-line
  interface TypographyOptions {
    alternateFontFamily: string;
  }
}

declare module "@material-ui/core/styles/createTheme" {
  // tslint:disable-next-line
  interface Theme {
    layout: ILayout;
  }

  // tslint:disable-next-line
  interface ThemeOptions {
    layout: ILayout;
  }
}

/*------------------------------*/
/*        Color Variables       */
/*------------------------------*/
// Un único acento en toda la app — ver DESIGN_SYSTEM.md §0. `secondary` se
// alinea al mismo acento (en vez de eliminar la clave, que rompería los ~130
// usos de color="secondary" fuera del alcance de este rediseño) para que no
// quede naranja en ningún sitio sin tocar página por página.
const primaryMain = "#0E7C86";
const primaryLight = "#E3F1F0";
const primaryDark = "#0A5E66";
const secondaryMain = primaryMain;
const secondaryDark = primaryDark;
const insightAccent = "#30a0ea";
const errorMain = "#b00020";
const errorLight = "#f9dbdc";
const textPrimary = "#10151A";
const textSecondary = "#5B6B67";
const contrastText = "#ffffff";
const subtleContrastText = "rgba(255, 255, 255, 0.69)";
const mediumGrey = "#757575";
const lightGrey = "#eef0f3";
const lighterGrey = "#f5f5f5";
const backgroundGrey = "#F4F6F5";
const lightBlue = "#94bed7";
const defaultBackground = "#fff";
const lightBorder = "rgba(255, 255, 255, 0.32)";
const lightBorderActive = "rgba(255, 255, 255, 0.82)";
const warning = "#f57c00";
const warningSoft = "rgba(245, 124, 0, 0.1)";
const success = "#2e7d32";
const successSoft = "rgba(46, 125, 50, 0.1)";
const infoSoft = "rgba(14, 124, 134, 0.1)";
const borderColor = "#E1E6E4";
const spiderGraphBackground = "rgba(13, 69, 93, 0.5)";
const accentSoft = "rgba(14, 124, 134, 0.08)";
const win = "#1B8A5A";
const winSoft = "#E3F3EA";
const draw = "#9A7A2C";
const drawSoft = "#F3EEDF";
const loss = "#B94438";
const lossSoft = "#F6E4E1";

/*------------------------------*/
/*         Font Variables       */
/*------------------------------*/
const soraFontFamily = '"Sora", "Helvetica", "Arial", sans-serif';
const plusJakartaFontFamily =
  '"Plus Jakarta Sans", "Helvetica", "Arial", sans-serif';

const theme = createTheme({
  layout: {
    navDrawerWidthOpen: 190,
    navDrawerWidthClosed: 72,
    maxContentWidth: 1280,
    headerHeight: 32,
    tabsHeight: 48,
    listHeaderBarHeight: 120,
    footerHeight: 80,
    menuDrawerWidth: 330,
  },
  zIndex: {
    drawer: 1050,
  },
  spacing: 8,
  palette: {
    extra: {
      default: "white",
      grey: lightGrey,
      mediumGrey,
      lighterGrey,
      lightBorder,
      lightBorderActive,
      borderColor,
      lightBlue,
      warning,
      warningSoft,
      success,
      successSoft,
      infoSoft,
      backgroundGrey,
      subtleContrastText,
      insightAccent,
      spiderGraphBackground,
      accentSoft,
      win,
      winSoft,
      draw,
      drawSoft,
      loss,
      lossSoft,
    },
    primary: {
      main: primaryMain,
      light: primaryLight,
      dark: primaryDark,
      contrastText,
    },
    error: {
      main: errorMain,
      light: errorLight,
    },
    secondary: {
      main: secondaryMain,
      dark: secondaryDark,
    },
    text: {
      primary: textPrimary,
      secondary: textSecondary,
    },
    divider: borderColor,
    background: {
      default: defaultBackground,
    },
  },
  typography: {
    fontFamily: plusJakartaFontFamily,
    alternateFontFamily: soraFontFamily,
    fontSize: 14,
    fontWeightLight: 300,
    fontWeightRegular: 400,
    fontWeightMedium: 500,
    fontWeightBold: 700,
    allVariants: {
      color: textPrimary,
      fontWeight: 400,
    },
    body1: {
      fontSize: 16,
      letterSpacing: 0.15,
      lineHeight: 1.6,
    },
    body2: {
      fontSize: 14,
      letterSpacing: 0.15,
      lineHeight: 1.55,
    },
    h1: {
      fontFamily: soraFontFamily,
      fontSize: 40,
      fontWeight: 700,
      letterSpacing: -0.5,
      lineHeight: 1.2,
    },
    h2: {
      fontFamily: soraFontFamily,
      fontSize: 32,
      fontWeight: 700,
      letterSpacing: -0.5,
      lineHeight: 1.25,
    },
    h3: {
      fontFamily: soraFontFamily,
      fontSize: 27,
      fontWeight: 600,
      lineHeight: 1.3,
    },
    h4: {
      fontFamily: soraFontFamily,
      fontSize: 23,
      fontWeight: 600,
      letterSpacing: 0,
      lineHeight: 1.3,
    },
    h5: {
      fontFamily: soraFontFamily,
      fontSize: 19,
      fontWeight: 600,
      lineHeight: 1.35,
    },
    h6: {
      fontFamily: soraFontFamily,
      fontSize: 17,
      fontWeight: 600,
      letterSpacing: 0.1,
      lineHeight: 1.4,
    },
    subtitle1: {
      fontSize: 16,
      fontWeight: 500,
      letterSpacing: 0.1,
    },
    subtitle2: {
      fontSize: 14,
      fontWeight: 500,
      letterSpacing: 0.1,
    },
    button: {
      fontSize: 14,
      fontWeight: 600,
      textTransform: "none",
      letterSpacing: 0.25,
      fontFamily: soraFontFamily,
    },
    caption: {
      fontSize: 12,
      letterSpacing: 0.3,
      color: textSecondary,
      fontFamily: soraFontFamily,
    },
    overline: {
      textTransform: "uppercase",
      fontFamily: soraFontFamily,
      fontSize: 11,
      fontWeight: 600,
      letterSpacing: 1.5,
      color: textSecondary,
    },
  },
  props: {
    MuiButton: {
      disableElevation: true,
    },
    MuiCard: {
      elevation: 0,
    },
  },
  overrides: {
    MuiCssBaseline: {
      "@global": {
        body: {
          backgroundColor: backgroundGrey,
        },
      },
    },
    MuiFormLabel: {
      root: {
        fontFamily: soraFontFamily,
        color: textSecondary,
        fontWeight: 500,
      },
    },
    MuiFormHelperText: {
      root: {
        fontFamily: soraFontFamily,
        margin: "4px 12px 0",
        fontWeight: 400,
        fontSize: 11.5,
        "&$error": {
          margin: "4px 12px 0",
        },
      },
      contained: {
        margin: "4px 12px 0",
      },
    },
    MuiMenuItem: {
      root: {
        fontFamily: plusJakartaFontFamily,
        fontSize: "15px",
        fontWeight: 400,
        "&:hover": {
          backgroundColor: "rgba(14, 124, 134, 0.06)",
          "& svg": {
            color: primaryMain,
          },
        },
        "& svg": {
          color: mediumGrey,
        },
      },
    },
    MuiLinearProgress: {
      root: {
        borderRadius: 4,
        height: 6,
      },
      bar1Determinate: {
        background: primaryMain,
        borderRadius: 4,
      },
    },
    MuiButton: {
      root: {
        padding: "10px 20px",
        fontSize: "14px",
        fontFamily: soraFontFamily,
        fontWeight: 600,
        lineHeight: 1.4,
        borderRadius: 10,
        transition: "all 0.2s ease",
      },
      contained: {
        boxShadow: "none",
        "&:hover": {
          boxShadow: "0 4px 12px rgba(0, 0, 0, 0.12)",
        },
      },
      containedPrimary: {
        "&:hover": {
          backgroundColor: primaryDark,
        },
      },
      outlined: {
        padding: "9px 20px",
        borderColor,
      },
      containedSecondary: {
        color: "white",
      },
      sizeSmall: {
        padding: "6px 14px",
        fontSize: "13px",
        borderRadius: 8,
      },
      sizeLarge: {
        padding: "13px 26px",
        fontSize: "15px",
        borderRadius: 12,
      },
      text: {
        padding: "10px 16px",
      },
    },
    MuiIconButton: {
      root: {
        borderRadius: 10,
      },
    },
    MuiSelect: {
      selectMenu: {
        paddingTop: 16,
        paddingBottom: 16,
      },
    },
    MuiTab: {
      root: {
        fontFamily: soraFontFamily,
        fontWeight: 500,
        textTransform: "none",
        fontSize: "14px",
        letterSpacing: 0.15,
      },
    },
    MuiTabs: {
      indicator: {
        height: 3,
        borderRadius: "3px 3px 0 0",
      },
    },
    MuiChip: {
      root: {
        color: primaryDark,
        borderColor,
        borderRadius: 8,
        height: 28,
        fontFamily: soraFontFamily,
        fontWeight: 500,
        fontSize: "12.5px",
        backgroundColor: accentSoft,
      },
      label: {
        paddingRight: 10,
        paddingLeft: 10,
      },
      avatar: {
        color: primaryMain,
        width: 27,
        height: 27,
        background: "transparent",
      },
      outlined: {
        backgroundColor: "transparent",
      },
    },
    MuiTextField: {
      root: {
        background: "white",
      },
    },
    MuiOutlinedInput: {
      root: {
        borderRadius: 10,
        "&:hover $notchedOutline": {
          borderColor: primaryMain,
        },
      },
      notchedOutline: {
        borderColor,
      },
    },
    MuiFilledInput: {
      root: {
        borderRadius: "10px 10px 0 0",
      },
    },
    MuiInputBase: {
      root: {
        fontWeight: 400,
      },
    },
    MuiCard: {
      root: {
        borderRadius: 16,
        border: `1px solid ${borderColor}`,
        boxShadow: "0 1px 3px rgba(16, 21, 26, 0.06)",
      },
    },
    MuiPaper: {
      rounded: {
        borderRadius: 16,
      },
      elevation1: {
        boxShadow: "0 1px 3px rgba(16, 21, 26, 0.08)",
      },
      elevation2: {
        boxShadow: "0 2px 8px rgba(16, 21, 26, 0.1)",
      },
      elevation3: {
        boxShadow: "0 4px 16px rgba(16, 21, 26, 0.1)",
      },
    },
    MuiDialog: {
      paper: {
        borderRadius: 16,
      },
    },
    MuiDialogTitle: {
      root: {
        "& h2": {
          fontFamily: soraFontFamily,
          fontWeight: 600,
        },
      },
    },
    MuiTableCell: {
      head: {
        fontFamily: soraFontFamily,
        fontWeight: 600,
        fontSize: "13px",
        color: textSecondary,
      },
      root: {
        borderBottom: `1px solid ${borderColor}`,
      },
    },
    MuiTooltip: {
      tooltip: {
        borderRadius: 8,
        fontSize: "12px",
        fontWeight: 400,
        backgroundColor: "rgba(16, 21, 26, 0.94)",
        padding: "6px 12px",
      },
    },
    MuiAvatar: {
      colorDefault: {
        backgroundColor: primaryLight,
        color: primaryDark,
        fontWeight: 600,
      },
    },
  },
});

export default theme;
